<template>
  <div class="school-data">
    <div class="school-data__header">
      <div class="school-data__header__left">
        <h1>Vikardækning</h1>
        <changeSchoolYear :white-theme="true" class="marg-1-right"></changeSchoolYear>
      </div>
      <div class="school-data__header__right">
        <p class="d-link" @click="resetDates" v-if="!isDatesPristine">{{ $t('vue2.task_overview.reset') }}</p>
        <Datepickerrange
          :startdate="startDate"
          :enddate="endDate"
          @startchanged="setStartDate"
          @endchanged="setEndDate">
        </Datepickerrange>
      </div>
    </div>
    <div class="loader-container" v-if="isLoading">
      <Loader></Loader>
      <h4>Snup en kop kaffe, det kan tage et par minutter</h4>
    </div>
    <template v-else>
      <p class="text-explainer">
        Berørte timer er lektioner, hvor en lærer har registreret fravær, eller hvor der er sat en vikar på.
        De fordeles på: dækket af vikar, medlærer til stede uden vikar, aflyst, ude af huset og ikke dækket.
        "% med vikarnoter" er andelen af lektioner med fravær, hvor der er skrevet vikarnoter.
      </p>
      <div class="school-data__toolbar">
        <Tabs :tabs="viewTabs" :selected="viewMode" @select="viewMode = $event"></Tabs>
        <Tabs :tabs="sortTabs" :selected="sortMode" @select="sortMode = $event"></Tabs>
      </div>
      <SchoolDataCoverageTable v-if="viewMode === 'grades'" title="Årgang" :rows="gradeRows"></SchoolDataCoverageTable>
      <SchoolDataCoverageTable v-if="viewMode === 'klasses'" title="Klasse" :rows="klassRows"></SchoolDataCoverageTable>
      <SchoolDataCoverageTable v-if="viewMode === 'subjects'" title="Fag" :rows="subjectRows"></SchoolDataCoverageTable>
    </template>
  </div>
</template>

<script>
import ChangeSchoolYear from '../School/ChangeSchoolYear'
import Loader from '../common/Loader'
import Tabs from '../common/Tabs.vue'
import Datepickerrange from '../datepicker/DatePickerRange'
import SchoolDataCoverageTable from './SchoolDataCoverageTable'
import schoolDataMixin from '../../mixins/schoolDataMixin'

const SCHOOL_TOTAL = 'Hele skolen'

export default {
  components: {Datepickerrange, Loader, ChangeSchoolYear, Tabs, SchoolDataCoverageTable},
  mixins: [schoolDataMixin],
  data () {
    return {
      viewMode: 'grades',
      sortMode: 'name',
      viewTabs: [
        {id: 'grades', title: 'Skole og årgange'},
        {id: 'klasses', title: 'Klasser'},
        {id: 'subjects', title: 'Fag'}
      ],
      sortTabs: [
        {id: 'name', title: 'Navn'},
        {id: 'substitute', title: 'Flest vikartimer'},
        {id: 'share', title: 'Højest andel vikar'},
        {id: 'uncovered', title: 'Flest ikke dækkede'}
      ]
    }
  },
  computed: {
    reportCalendars () {
      const userCalendars = this.$store.getters.userCalendars || []
      const klassCalendars = this.$store.getters.klassCalendars || []
      const clubCalendars = this.$store.getters.clubCalendars || []
      return userCalendars.concat(klassCalendars, clubCalendars)
    },
    affectedByLessonId () {
      const hash = {}
      this.affectedLessons.forEach(item => {
        hash[item.lesson.event_id] = item
      })
      return hash
    },
    gradeRows () {
      const rows = this.aggregate(lesson => {
        const grades = this.getLessonKlasses(lesson).map(klass => this.getGradeName(klass.name))
        return [SCHOOL_TOTAL].concat(grades)
      })
      return this.sortRows(rows, (a, b) => this.gradeNumber(a.name) - this.gradeNumber(b.name))
    },
    klassRows () {
      const rows = this.aggregate(lesson => this.getLessonKlasses(lesson).map(klass => klass.name))
      return this.sortRows(rows, (a, b) => a.name.localeCompare(b.name, 'da', {numeric: true}))
    },
    subjectRows () {
      const rows = this.aggregate(lesson => [lesson.title || 'Uden titel'])
      return this.sortRows(rows, (a, b) => a.name.localeCompare(b.name, 'da'))
    }
  },
  methods: {
    aggregate (getKeys) {
      const rowsByName = {}
      this.lessonsInRange.forEach(lesson => {
        const minutes = this.getLessonMinutes(lesson)
        const affected = this.affectedByLessonId[lesson.event_id]
        const keys = getKeys(lesson).filter((key, idx, arr) => arr.indexOf(key) === idx)
        keys.forEach(key => {
          if (!rowsByName[key]) {
            rowsByName[key] = {
              name: key,
              isTotal: key === SCHOOL_TOTAL,
              totalMinutes: 0,
              affectedMinutes: 0,
              substitute: 0,
              coTeacher: 0,
              canceled: 0,
              away: 0,
              uncovered: 0,
              lessonsWithAbsence: 0,
              lessonsWithNotes: 0
            }
          }
          const row = rowsByName[key]
          row.totalMinutes += minutes
          if (affected) {
            row.affectedMinutes += minutes
            row[affected.coverage] += minutes
            if (affected.absentTeachers.length > 0) {
              row.lessonsWithAbsence += 1
              if (affected.hasTeacherNotes) {
                row.lessonsWithNotes += 1
              }
            }
          }
        })
      })
      return Object.values(rowsByName)
    },
    sortRows (rows, nameCompare) {
      const compare = {
        name: nameCompare,
        substitute: (a, b) => b.substitute - a.substitute,
        share: (a, b) => this.share(b.substitute, b.totalMinutes) - this.share(a.substitute, a.totalMinutes),
        uncovered: (a, b) => b.uncovered - a.uncovered
      }[this.sortMode]
      return rows.slice().sort((a, b) => {
        if (a.isTotal !== b.isTotal) {
          return a.isTotal ? -1 : 1
        }
        return compare(a, b)
      })
    },
    share (part, total) {
      return total ? part / total : 0
    },
    gradeNumber (name) {
      const match = name.match(/^(\d+)/)
      return match ? match[1] * 1 : 999
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.school-data {
  margin: 1rem;
  max-width: 1440px;
}

.school-data__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 1100px;
}

.school-data__header__left,
.school-data__header__right {
  display: flex;
  align-items: center;
}

.school-data__header h1 {
  margin-right: 1rem;
}

.school-data__toolbar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.text-explainer {
  max-width: 900px;
  color: #728195;
  margin-bottom: 1rem;
}

.loader-container {
  text-align: center;
}

.d-link {
  color: var(--blue);
  cursor: pointer;
  margin-right: 1rem;
}

.d-link:hover {
  text-decoration: underline;
}
</style>



// WEBPACK FOOTER //
// SchoolDataCoverage.vue?18dec684